import React, { useState, useMemo } from 'react';
import { Card, Text, Flex, Badge } from '@tremor/react';
import {
  TableCellsIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const ClosedDealsTable = ({ data }) => {
  const [sortField, setSortField] = useState('close_date_raw');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(10);

  const filteredAndSorted = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = data;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        (d.dealname || '').toLowerCase().includes(q) ||
        (d.owner_name || '').toLowerCase().includes(q)
      );
    }

    // Sort
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'amount' || sortField === 'days_to_close') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '');
        bVal = String(bVal || '');
      }

      if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
  }, [data, sortField, sortDirection, searchQuery]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDownIcon className="h-3 w-3 text-gray-300" />;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="h-3 w-3 text-gray-600" />
      : <ChevronDownIcon className="h-3 w-3 text-gray-600" />;
  };

  if (!data || data.length === 0) {
    return (
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
        <div className="flex items-center justify-center h-48 text-gray-400">
          <TableCellsIcon className="h-8 w-8 mr-2" />
          <span>No closed deals in the last 6 months</span>
        </div>
      </Card>
    );
  }

  const visibleData = filteredAndSorted.slice(0, visibleCount);
  const hasMore = visibleCount < filteredAndSorted.length;

  const columns = [
    { key: 'month', label: 'Month', sortable: false },
    { key: 'dealname', label: 'Deal Name', sortable: false },
    { key: 'amount', label: 'Amount', sortable: true },
    { key: 'owner_name', label: 'Owner', sortable: false },
    { key: 'days_to_close', label: 'Days to Close', sortable: true },
    { key: 'close_date_raw', label: 'Close Date', sortable: true },
  ];

  return (
    <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
      {/* Header */}
      <Flex justifyContent="between" alignItems="start" className="mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-white/60 rounded-xl">
            <TableCellsIcon className="h-5 w-5 text-[#809292]" />
          </div>
          <div>
            <Text className="font-semibold text-gray-900">Closed Won Deals</Text>
            <Text className="text-xs text-gray-500">
              {filteredAndSorted.length} deal{filteredAndSorted.length !== 1 ? 's' : ''} in the last 6 months
            </Text>
          </div>
        </div>
        <Badge color="emerald" size="sm">
          {formatCurrency(data.reduce((sum, d) => sum + (d.amount || 0), 0))} Total
        </Badge>
      </Flex>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by deal name or owner..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(10); }}
          className="w-full pl-10 pr-4 py-2.5 bg-white/60 border border-gray-200/50 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00CBC0]/30 focus:border-[#00CBC0]/50 transition-all"
        />
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3">
        {visibleData.map((deal, idx) => (
          <div key={idx} className="bg-white/60 rounded-xl p-4 border border-gray-100">
            <Text className="font-medium text-gray-900 text-sm mb-2 truncate">{deal.dealname}</Text>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <Text className="text-gray-400">Amount</Text>
                <Text className="font-mono font-medium text-emerald-600">{formatCurrency(deal.amount)}</Text>
              </div>
              <div>
                <Text className="text-gray-400">Owner</Text>
                <Text className="text-gray-700">{deal.owner_name}</Text>
              </div>
              <div>
                <Text className="text-gray-400">Days to Close</Text>
                <Text className="font-mono text-gray-700">{deal.days_to_close}d</Text>
              </div>
              <div>
                <Text className="text-gray-400">Close Date</Text>
                <Text className="text-gray-700">{deal.close_date_formatted}</Text>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50/90 backdrop-blur-sm">
                {columns.map(col => (
                  <th
                    key={col.key}
                    className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <Flex alignItems="center" className="gap-1">
                      {col.label}
                      {col.sortable && <SortIcon field={col.key} />}
                    </Flex>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleData.map((deal, idx) => (
                <tr key={idx} className="hover:bg-white/40 transition-colors">
                  <td className="px-4 py-3">
                    <Badge color="slate" size="xs">{deal.month}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Text className="text-gray-900 font-medium truncate max-w-[200px]">{deal.dealname}</Text>
                  </td>
                  <td className="px-4 py-3">
                    <Text className="font-mono font-medium text-emerald-600">{formatCurrency(deal.amount)}</Text>
                  </td>
                  <td className="px-4 py-3">
                    <Text className="text-gray-700">{deal.owner_name}</Text>
                  </td>
                  <td className="px-4 py-3">
                    <Text className="font-mono text-gray-700">{deal.days_to_close}d</Text>
                  </td>
                  <td className="px-4 py-3">
                    <Text className="text-gray-600">{deal.close_date_formatted}</Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setVisibleCount(c => c + 10)}
            className="px-4 py-2 text-sm font-medium text-[#00CBC0] hover:text-[#00b0a6] hover:bg-[#00CBC0]/5 rounded-xl transition-all"
          >
            Show more ({filteredAndSorted.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </Card>
  );
};

export default ClosedDealsTable;
